import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActionCd, PayformNoteTypeCd, PayformStatusCd, Person } from '@xpo-ltl/sdk-common';
import {
  ApproveLinehaulPayformResp,
  ApproveLinehaulPayformRqst,
  DeleteLinehaulPayformRqst,
  DsrActivity,
  DsrPayform,
  LinehaulPayformApiService,
  LinehaulSchedule,
  Note,
  UpsertLinehaulPayformResp,
  UpsertLinehaulPayformRqst,
} from '@xpo-ltl/sdk-linehaulpayform';
import * as _ from 'lodash';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { AppConstantsService } from '../../../services/app-constants.service';
import { NotificationService } from '@xpo-ltl/data-api';
import { ConfigManagerService } from '@xpo-ltl/config-manager';
import { ConfigManagerProperties } from '../../../enums';
import { Store } from '@ngrx/store';
import { currentPayformSelector } from '@app/state/payform';
import { AppStateUtils } from '@app/state';
import { deleteActivityAction } from '@app/state/activities';
import { addNoteAction, deleteNoteAction } from '@app/state/notes';
import { deleteScheduleAction } from '@app/state/schedules';

@Injectable()
export class PayFormService {
  private payformFormGroupSubject = new BehaviorSubject<FormGroup>(undefined);
  public payformFormGroup$ = this.payformFormGroupSubject.asObservable();
  public currentPayform: DsrPayform;

  private nextSequenceNumbers: {
    schSequenceNbr: number;
    noteSequenceNbr: number;
    actvtySequenceNbr: number;
  } = {
    schSequenceNbr: 0,
    noteSequenceNbr: 0,
    actvtySequenceNbr: 0,
  };

  constructor(
    private constants: AppConstantsService,
    private linehaulService: LinehaulPayformApiService,
    private notificationService: NotificationService,
    private config: ConfigManagerService,
    private store: Store
  ) {
    this.store.select(currentPayformSelector).subscribe(payform => {
      this.currentPayform = payform;
      this.calculateNextSequenceNumbers(payform);
    });
  }

  private calculateNextSequenceNumbers(payform: DsrPayform): void {
    this.nextSequenceNumbers = {
      schSequenceNbr: _.max([0, ..._.map(payform.linehaulSchedule, sch => +sch.schSequenceNbr)]),
      noteSequenceNbr: _.max([0, ..._.map(payform.note, sch => +sch.noteSequenceNbr)]),
      actvtySequenceNbr: _.max([0, ..._.map(payform.dsrActivity, sch => +sch.actvtySequenceNbr)]),
    };
  }

  public nextSchSequenceNbr(): string {
    this.nextSequenceNumbers.schSequenceNbr += 1;
    return String(this.nextSequenceNumbers.schSequenceNbr);
  }

  public nextNoteSequenceNbr(): string {
    this.nextSequenceNumbers.noteSequenceNbr += 1;
    return String(this.nextSequenceNumbers.noteSequenceNbr);
  }

  public nextActvtySequenceNbr(): string {
    this.nextSequenceNumbers.actvtySequenceNbr += 1;
    return String(this.nextSequenceNumbers.actvtySequenceNbr);
  }

  /**
   * Create a new Note with the current User as commenter
   */
  public createNote(noteType: PayformNoteTypeCd, noteTypeSequenceNbr: string | number, comments: string): Note {
    const note = new Note();
    note.noteSequenceNbr = this.nextNoteSequenceNbr();
    note.dsrPayformId = this.currentPayform.dsrPayformId;
    note.noteDateTimeUtc = new Date();
    note.noteType = noteType;
    note.noteTypeSequenceNbr = noteTypeSequenceNbr ? String(noteTypeSequenceNbr) : undefined;
    note.comments = comments;

    const user = this.constants.user;
    note.commenterEmployeeDetail = new Person();
    note.commenterEmployeeDetail.firstName = user.givenName;
    note.commenterEmployeeDetail.lastName = user.lastName;
    note.commenterEmployeeDetail.fullName = `${user.lastName},${user.givenName}`;
    return note;
  }

  /**
   * Delete the DsrActivity and any associated Data
   * @param actvtySequenceNbr Id of activity to delete
   */
  public deleteActivity(actvtySequenceNbr: string) {
    // delete any Notes associated with this activity
    _.forEach(this.currentPayform.note, note => {
      if (note.noteType === PayformNoteTypeCd.ACTIVITY && note.noteTypeSequenceNbr === actvtySequenceNbr) {
        this.store.dispatch(deleteNoteAction({ noteSequenceNbr: note.noteSequenceNbr }));
      }
    });

    // finally, delete the Activity
    this.store.dispatch(deleteActivityAction({ actvtySequenceNbr }));
  }

  /**
   * Delete the Schedule and any associated Data (ie, DsrActivities, etc)
   * @param schSequenceNbr Schedule id to delete
   */
  public deleteSchedule(schSequenceNbr: string) {
    // first, delete all Activities for this schedule
    _.forEach(this.currentPayform.dsrActivity, act => {
      if (act.schSequenceNbr === schSequenceNbr) {
        this.deleteActivity(act.actvtySequenceNbr);
      }
    });

    // finally, delete the schedule
    this.store.dispatch(
      deleteScheduleAction({
        schSequenceNbr,
      })
    );
  }

  /**
   * Takes the passed payform and returns a new one that is in the correct format for upserting
   *
   * @param payform payform to prepare for upsert
   */
  private prepareForUpsert(sourcePayform: DsrPayform): DsrPayform {
    const sourceAsString = JSON.stringify(sourcePayform);

    // make a deep copy.  We don't want ANY changes to get reflected in the sourcePayform!
    const payform = JSON.parse(sourceAsString) as DsrPayform;

    // set submit time to current local time
    payform.submtDateTimeUtc = new Date();

    // Make sure that we have an AuditInfo for everything that requires it
    _.forEach(payform.linehaulSchedule, (schedule: LinehaulSchedule) => {
      if (schedule.scheduleDetour) {
        // make sure ScheduleDetour has an AuditInfo.  Upsert fails if it doesn't have one.
        schedule.scheduleDetour.auditInfo = {
          ...payform.auditInfo,
          ...schedule.scheduleDetour.auditInfo,
        };
      }
    });

    // Replace default dsrPayformId with undefined
    this.removeDefaultDsrPayformId(payform);

    // strip name from auditInfo
    this.stripNamesFromIds(payform);

    // Move new Notes to correct sections
    this.moveNotes(payform);

    return payform;
  }

  // Remove all names from ids ( ie, '1234|John Smith' => '1234')
  private stripNamesFromIds(payform: DsrPayform): DsrPayform {
    const getId = id => (id ? id.split('|')[0] : undefined);

    const fixAuditInfo = (item: any) => {
      const orig = _.get(item, 'auditInfo');
      const isPayform = item.versionNbr !== undefined;
      if (orig) {
        const ai = {
          ...orig,
          createdById: getId(orig.createdById),
          updateById: getId(orig.updateById),
        };

        if (isPayform) {
          ai.updateById = undefined;
          ai.updateByPgmId = this.constants.programId;
        }
        _.set(item, 'auditInfo', ai);
      }
    };

    fixAuditInfo(payform);
    _.forEach(payform.dsrActivity, (activity: DsrActivity) => {
      fixAuditInfo(activity);
      fixAuditInfo(activity.nonDriveTimeType);
    });

    _.forEach(payform.note, (note: Note) => fixAuditInfo(note));

    _.forEach(payform.linehaulSchedule, (schedule: LinehaulSchedule) => {
      fixAuditInfo(schedule);
      fixAuditInfo(schedule.scheduleDetour);
      _.forEach(schedule.scheduleTrailer, trailer => fixAuditInfo(trailer));
    });

    return payform;
  }

  private removeDefaultDsrPayformId(payform: DsrPayform): DsrPayform {
    payform.dsrPayformId = payform.dsrPayformId === this.constants.NewPayformId ? undefined : payform.dsrPayformId;

    const removeDefault = item => {
      if (item.dsrPayformId === this.constants.NewPayformId) {
        item.dsrPayformId = undefined;
      }
    };

    _.forEach(payform.note, removeDefault);
    _.forEach(payform.linehaulSchedule, removeDefault);
    _.forEach(payform.dsrActivity, removeDefault);

    return payform;
  }

  private moveNotes(payform: DsrPayform): DsrPayform {
    // get list of all new, non-PAYFORM notes
    const addedNotes = _.remove(payform.note, note => note.noteType !== PayformNoteTypeCd.PAYFORM);
    _.forEach(addedNotes, note => {
      switch (note.noteType) {
        case PayformNoteTypeCd.ACTIVITY:
          const activity = AppStateUtils.getActivityFromState(payform.dsrActivity, note.noteTypeSequenceNbr);
          if (activity) {
            activity.note = _.defaultTo(activity.note, []);
            activity.note.push(note);
          }
          break;

        case PayformNoteTypeCd.DETOUR:
          // TODO - move notes
          break;

        case PayformNoteTypeCd.SCHEDULE:
          // TODO - move notes
          break;
      }
    });

    return payform;
  }

  /**
   * Upsert the current Payform as SUBMITTED
   */
  public submitPayform(feedback: string): Observable<UpsertLinehaulPayformResp> {
    if (feedback) {
      // add feedback as a Note to the payform
      const feedbackNote = this.createNote(PayformNoteTypeCd.PAYFORM, null, feedback);
      this.store.dispatch(addNoteAction({ note: feedbackNote }));
    }

    const payform = this.prepareForUpsert(this.currentPayform);
    payform.statusCd = PayformStatusCd.SUBMITTED;
    if (feedback) {
      payform.supvsrEditedInd = true;
    }

    const request = new UpsertLinehaulPayformRqst();
    request.dsrPayform = payform;

    return this.linehaulService.upsertLinehaulPayform(request).pipe(take(1));
  }

  /**
   * DELETED the current Payform
   */
  public deletePayform(feedback: string): Observable<boolean> {
    const subject = new Subject<boolean>();
    const complete = result => {
      subject.next(result);
      subject.complete();
    };

    // add feedback as a Note to the payform
    const feedbackNote = this.createNote(PayformNoteTypeCd.PAYFORM, null, feedback);
    this.store.dispatch(addNoteAction({ note: feedbackNote }));

    const payform = this.prepareForUpsert(this.currentPayform);
    payform.supvsrEditedInd = true;

    const upsertRequest = new UpsertLinehaulPayformRqst();
    upsertRequest.dsrPayform = payform;
    this.linehaulService
      .upsertLinehaulPayform(upsertRequest, { toastOnError: false })
      .pipe(take(1))
      .subscribe(
        () => {
          const deleteRequest = new DeleteLinehaulPayformRqst();
          deleteRequest.dsrPayformId = this.currentPayform.dsrPayformId;
          this.linehaulService
            .deleteLinehaulPayform(deleteRequest)
            .pipe(take(1))
            .subscribe(
              () => {
                complete(true);
              },
              () => complete(false)
            );
        },
        error => {
          this.showError(error);
          complete(false);
        }
      );

    return subject;
  }

  /**
   * Restore the payform from DELETED status to NEW status
   */
  public restorePayform(): Observable<UpsertLinehaulPayformResp> {
    const payform = this.prepareForUpsert(this.currentPayform);
    payform.statusCd = payform.timeCrdNbr1 || payform.timeCrdNbr2 ? PayformStatusCd.SUBMITTED : PayformStatusCd.NEW;
    payform.supvsrEditedInd = true;

    const request = new UpsertLinehaulPayformRqst();
    request.dsrPayform = payform;
    return this.linehaulService.upsertLinehaulPayform(request).pipe(take(1));
  }

  /**
   * Add comment to current Payform and upsert it as RETURNED
   * @param comments Note to add to the payform when returning it
   */
  public returnPayform(feedback: string): Observable<UpsertLinehaulPayformResp> {
    // add feedback as a Note to the payform
    const feedbackNote = this.createNote(PayformNoteTypeCd.PAYFORM, null, feedback);
    this.store.dispatch(addNoteAction({ note: feedbackNote }));

    const payform = this.prepareForUpsert(this.currentPayform);
    payform.statusCd = PayformStatusCd.RETURNED;

    const request = new UpsertLinehaulPayformRqst();
    request.dsrPayform = payform;
    return this.linehaulService.upsertLinehaulPayform(request).pipe(take(1));
  }

  public approvePayform(feedback?: string): Observable<ApproveLinehaulPayformResp> {
    const subject = new Subject<ApproveLinehaulPayformResp>();

    const completed = results => {
      subject.next(results);
      subject.complete();
    };

    if (feedback) {
      // add feedback as a Note to the payform
      const feedbackNote = this.createNote(PayformNoteTypeCd.PAYFORM, null, feedback);
      this.store.dispatch(addNoteAction({ note: feedbackNote }));

      const payform = this.prepareForUpsert(this.currentPayform);
      payform.statusCd = PayformStatusCd.SUBMITTED;
      payform.supvsrEditedInd = true;

      const upsertRequest = new UpsertLinehaulPayformRqst();
      upsertRequest.dsrPayform = payform;
      this.linehaulService
        .upsertLinehaulPayform(upsertRequest, { toastOnError: false })
        .pipe(take(1))
        .subscribe(
          upsertResults => {
            if (upsertResults) {
              const approveRequest = new ApproveLinehaulPayformRqst();
              approveRequest.dsrPayform = upsertResults.dsrPayform;
              this.linehaulService
                .approveLinehaulPayform(approveRequest)
                .pipe(take(1))
                .subscribe(results => {
                  completed(results);
                });
            } else {
              completed(undefined);
            }
          },
          error => {
            this.showError(error);
            completed(undefined);
          }
        );
    } else {
      const payform = this.prepareForUpsert(this.currentPayform);
      payform.statusCd = PayformStatusCd.SUBMITTED;

      const request = new ApproveLinehaulPayformRqst();
      request.dsrPayform = payform;
      this.linehaulService
        .approveLinehaulPayform(request, { toastOnError: false })
        .pipe(take(1))
        .subscribe(
          results => {
            completed(results);
          },
          error => {
            this.showError(error);
            completed(undefined);
          }
        );
    }

    return subject;
  }

  private showError(error): void {
    const errorMessage = _.get(error, 'error.moreInfo[0].message') || error.message;

    this.notificationService.showSnackBarMessage(errorMessage, {
      actionHandler: null,
      durationInMillis: this.config.getSetting<number>(ConfigManagerProperties.errorToastDuration),
      status: 'Error',
    });
  }
}
